<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <v-card outlined class="benefit-decision-panel d-flex flex-column">
    <!-- Read-only: decision already made -->
    <template v-if="!isPending">
      <v-card-text class="flex-grow-1">
        <v-alert
          outlined
          class="mb-4"
        >
          <div class="d-flex align-center">
            <strong class="mr-2">Решение:</strong>
            <benefit-status-chip :status="item.status" />
          </div>
        </v-alert>

        <div class="readonly-grid">
          <div v-if="item.resolved_at" class="readonly-row">
            <span class="readonly-label text--secondary">Дата решения</span>
            <span>{{ formatDateTime(item.resolved_at) }}</span>
          </div>
          <div v-if="item.resolved_by_name" class="readonly-row">
            <span class="readonly-label text--secondary">Кто принял</span>
            <span>{{ item.resolved_by_name }}</span>
          </div>
          <div v-if="item.rejection_reason" class="readonly-row">
            <span class="readonly-label text--secondary">Причина отклонения</span>
            <span>{{ item.rejection_reason }}</span>
          </div>
          <div v-if="item.comment" class="readonly-row">
            <span class="readonly-label text--secondary">Комментарий</span>
            <span>{{ item.comment }}</span>
          </div>
        </div>

        <div class="mt-4">
          <v-btn small outlined @click="$emit('open-logs')">
            <v-icon left small>
              {{ mdiHistory }}
            </v-icon>
            Посмотреть логи
          </v-btn>
        </div>
      </v-card-text>
    </template>

    <!-- Active decision form -->
    <template v-else>
      <v-tabs v-model="tab" class="decision-tabs flex-shrink-0">
        <v-tab>
          <v-icon left small color="success">
            {{ mdiCheckCircle }}
          </v-icon>
          Одобрить
        </v-tab>
        <v-tab>
          <v-icon left small color="error">
            {{ mdiCloseCircle }}
          </v-icon>
          Отклонить
        </v-tab>
      </v-tabs>
      <v-divider />

      <v-tabs-items v-model="tab" class="decision-tab-panels flex-grow-1">
        <!-- Approve tab -->
        <v-tab-item>
          <v-card-text class="decision-tab-body">
            <v-select
              v-model="form.category_id"
              :items="categoryItems"
              item-text="name"
              item-value="id"
              label="Категория льготы *"
              outlined
              dense
              clearable
              :error-messages="approveCategoryError"
              class="mb-3"
            />

            <v-row dense class="mb-1">
              <v-col cols="12" sm="6">
                <v-menu v-model="menuFrom" :close-on-content-click="false" offset-y>
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.active_from"
                      label="Действует с"
                      readonly
                      clearable
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="form.active_from" @input="menuFrom = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu v-model="menuTo" :close-on-content-click="false" offset-y>
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.active_to"
                      label="Действует по"
                      readonly
                      clearable
                      outlined
                      dense
                      :error-messages="dateRangeError"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="form.active_to" @input="menuTo = false" />
                </v-menu>
              </v-col>
            </v-row>

            <v-textarea
              v-model="form.comment"
              label="Комментарий менеджера (необязательно)"
              rows="2"
              outlined
              dense
              class="mb-3"
            />
            <v-file-input
              v-model="form.document"
              label="Документ для льготы (необязательно)"
              outlined
              dense
              show-size
            />
          </v-card-text>
        </v-tab-item>

        <!-- Reject tab -->
        <v-tab-item>
          <v-card-text class="decision-tab-body">
            <v-textarea
              v-model="form.rejection_reason"
              label="Причина отклонения *"
              rows="4"
              outlined
              :error-messages="rejectReasonError"
              class="mb-2"
            />
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <!-- Sticky action footer -->
      <v-divider />
      <v-card-actions class="decision-actions pa-3">
        <v-spacer />
        <v-btn
          :color="tab === 0 ? 'success' : 'error'"
          :loading="loading"
          :disabled="!item"
          @click="submit"
        >
          <v-icon left>
            {{ tab === 0 ? mdiCheckCircle : mdiCloseCircle }}
          </v-icon>
          {{ tab === 0 ? 'Одобрить заявку' : 'Отклонить заявку' }}
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import { mdiCheckCircle, mdiCloseCircle, mdiHistory } from '@mdi/js'
import { formatDateTime } from '../utils'
import BenefitStatusChip from './BenefitStatusChip.vue'

export default {
  name: 'BenefitDecisionPanel',
  components: { BenefitStatusChip },
  props: {
    item: {
      type: Object,
      default: null,
    },
    categoryItems: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    initialCategoryId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      mdiCheckCircle,
      mdiCloseCircle,
      mdiHistory,
      tab: 0,
      menuFrom: false,
      menuTo: false,
      approveTouched: false,
      rejectTouched: false,
      form: {
        category_id: this.initialCategoryId || '',
        active_from: '',
        active_to: '',
        comment: '',
        rejection_reason: '',
        document: null,
      },
    }
  },
  computed: {
    isPending() {
      return !this.item || this.item.status === 'pending'
    },
    approveCategoryError() {
      if (!this.approveTouched) return []
      return this.form.category_id ? [] : ['Выберите категорию льготы']
    },
    rejectReasonError() {
      if (!this.rejectTouched) return []
      return this.form.rejection_reason.trim() ? [] : ['Укажите причину отклонения']
    },
    dateRangeError() {
      if (!this.form.active_from || !this.form.active_to) return []
      return this.form.active_from <= this.form.active_to
        ? []
        : ['Дата окончания не может быть раньше даты начала']
    },
  },
  watch: {
    initialCategoryId(val) {
      if (val && !this.form.category_id) {
        this.form.category_id = val
      }
    },
  },
  methods: {
    formatDateTime,
    submit() {
      if (this.tab === 0) {
        this.approveTouched = true
        if (!this.form.category_id) return
        if (this.dateRangeError.length) return
        this.$emit('approve', { ...this.form })
      } else {
        this.rejectTouched = true
        if (!this.form.rejection_reason.trim()) return
        this.$emit('reject', { ...this.form })
      }
    },
    resetForm() {
      this.form = {
        category_id: this.initialCategoryId || '',
        active_from: '',
        active_to: '',
        comment: '',
        rejection_reason: '',
        document: null,
      }
      this.approveTouched = false
      this.rejectTouched = false
    },
  },
}
</script>

<style scoped>
/* Отступ между полоской вкладок и полями формы */
.decision-tab-body {
  padding-top: 20px !important;
}

.decision-actions {
  position: sticky;
  bottom: 0;
  background: var(--v-background-base, #fff);
  z-index: 1;
}
.readonly-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.readonly-row {
  display: flex;
  gap: 12px;
}
.readonly-label {
  min-width: 140px;
  flex-shrink: 0;
  font-size: 0.875rem;
}
</style>
